import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Megaphone, CalendarDays, BellRing } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { siteService, resolveUploadUrl } from '@shared/libs/services'
import type { AnnouncementType, SiteAnnouncement } from '@shared/libs/services'
import Lightbox from '@shared/components/Lightbox'


interface Slide {
    id: string
    type: AnnouncementType
    title: string
    body?: string | null
    meta?: string | null
    linkUrl?: string | null
    linkLabel?: string | null
    image?: string | null
}

const TYPE_STYLES: Record<AnnouncementType, { label: string; color: string; bg: string; Icon: LucideIcon }> = {
    cfp: { label: 'Call for Papers', color: '#9e2b25', bg: 'rgba(158,43,37,0.08)', Icon: Megaphone },
    event: { label: 'Event', color: '#2f6b46', bg: 'rgba(47,107,70,0.08)', Icon: CalendarDays },
    notice: { label: 'Notice', color: '#8a6414', bg: 'rgba(138,100,20,0.08)', Icon: BellRing }
}

const FALLBACK_SLIDES: Slide[] = [
    {
        id: 'fallback-cfp',
        type: 'cfp',
        title: 'Volume 7 · Issue 2 — submissions open',
        body: 'We invite original research and review essays on Indian federalism, electoral behaviour and public policy.',
        meta: 'Open year-round · No deadline',
        linkUrl: '/call-for-papers',
        linkLabel: 'View call for papers',
        image: '/uploads/2026/10/Call_For_Papers_ISR_Vol7_No_II_2026_page-0001.jpg'
    },
    {
        id: 'fallback-notice',
        type: 'notice',
        title: 'Vol. 7 No. 1 (2026) is now published',
        body: 'The January – June 2026 issue of Indian Studies Review is available open-access — read the full issue online.',
        meta: 'Latest issue · January – June 2026',
        linkUrl: '/current-issue',
        linkLabel: 'Read current issue'
    },
    {
        id: 'fallback-event',
        type: 'event',
        title: 'Seminars, webinars & convenings',
        body: 'The Centre convenes scholars, practitioners and policymakers in rigorous public dialogue on politics and governance.',
        meta: 'Events & collaboration',
        linkUrl: '/events-collaboration',
        linkLabel: 'Explore events',
        image: '/uploads/events/webinar-participants.webp'
    }
]

function SlideCta({ slide }: { slide: Slide }) {
    if (!slide.linkUrl) return null
    const label = slide.linkLabel || 'Learn more'
    const cls = 'link-arrow mt-4 inline-flex'
    if (/^https?:\/\//i.test(slide.linkUrl)) {
        return (
            <a href={slide.linkUrl} target="_blank" rel="noreferrer" className={cls}>
                {label} →
            </a>
        )
    }
    return (
        <Link to={slide.linkUrl} className={cls}>
            {label} →
        </Link>
    )
}

function SlideImage({ slide, onOpen }: { slide: Slide; onOpen: () => void }) {
    if (!slide.image) return null
    return (
        <button
            type="button"
            onClick={onOpen}
            className="block w-full group px-5 pt-5 cursor-zoom-in"
            aria-label={`View ${slide.title} in full`}
        >
            <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-auto transition-transform duration-300 group-hover:scale-[1.015]"
                style={{
                    maxHeight: '24rem',
                    objectFit: 'contain',
                    background: '#fff',
                    border: '1px solid var(--color-rule)',
                    boxShadow: '0 14px 34px -22px rgba(22,20,16,0.45)'
                }}
                decoding="async"
            />
        </button>
    )
}

export default function HeroCarousel() {
    const reduced = useReducedMotion()
    const [slides, setSlides] = useState<Slide[]>(FALLBACK_SLIDES)
    const [index, setIndex] = useState(0)
    const [paused, setPaused] = useState(false)
    const [viewing, setViewing] = useState<Slide | null>(null)

    useEffect(() => {
        let cancelled = false
        siteService
            .listAnnouncements()
            .then((items: SiteAnnouncement[]) => {
                if (cancelled || !items.length) return
                setSlides(
                    items.map((a) => ({
                        id: a.id,
                        type: (['cfp', 'event', 'notice'] as const).includes(a.type) ? a.type : 'notice',
                        title: a.title,
                        body: a.body,
                        meta: a.meta,
                        linkUrl: a.linkUrl,
                        linkLabel: a.linkLabel,
                        image: resolveUploadUrl(a.image)
                    }))
                )
                setIndex(0)
            })
            .catch(() => {
            })
        return () => {
            cancelled = true
        }
    }, [])

    const count = slides.length
    useEffect(() => {
        if (reduced || paused || viewing || count <= 1) return
        const id = setInterval(() => setIndex((i) => (i + 1) % count), 5000)
        return () => clearInterval(id)
    }, [reduced, paused, viewing, count])

    const slide = slides[Math.min(index, count - 1)]
    const t = TYPE_STYLES[slide.type]
    const go = (dir: 1 | -1) => setIndex((i) => (i + dir + count) % count)

    const progressKey = useMemo(() => `${slide.id}-${index}`, [slide.id, index])

    return (
        <div
            className="flex flex-col"
            style={{ border: '1px solid var(--color-rule-strong)', background: 'var(--color-paper-50)' }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            aria-roledescription="carousel"
            aria-label="Announcements"
        >
            <div
                className="flex items-center justify-between px-5 py-3"
                style={{ background: 'var(--color-gold-800)', color: 'var(--color-paper-100)' }}
            >
                <span className="text-[11px] uppercase font-semibold" style={{ letterSpacing: '0.22em' }}>
                    Announcements
                </span>
                <span className="text-[10px] uppercase font-semibold" style={{ letterSpacing: '0.18em', color: 'var(--color-gold-300)' }}>
                    {String(index + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
                </span>
            </div>

            <div aria-hidden style={{ height: 2, background: 'var(--color-rule)' }}>
                {!reduced && count > 1 && (
                    <motion.div
                        key={progressKey}
                        initial={{ width: '0%' }}
                        animate={{ width: paused ? undefined : '100%' }}
                        transition={{ duration: 5, ease: 'linear' }}
                        style={{ height: '100%', background: t.color }}
                    />
                )}
            </div>

            <div className="relative flex-1 overflow-hidden" style={{ minHeight: '21rem' }}>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={slide.id}
                        initial={reduced ? { opacity: 0 } : { opacity: 0, x: 28 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, x: -28 }}
                        transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                        className="flex flex-col h-full"
                    >
                        <SlideImage slide={slide} onOpen={() => setViewing(slide)} />
                        <div className="p-5 sm:p-6 flex flex-col items-start flex-1">
                            <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase font-semibold"
                                style={{ letterSpacing: '0.14em', color: t.color, background: t.bg, border: `1px solid ${t.color}` }}
                            >
                                <t.Icon size={11} strokeWidth={2} />
                                {t.label}
                            </span>
                            {slide.meta && (
                                <div
                                    className="mt-3 text-[10.5px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.16em', color: 'var(--color-ink-400)' }}
                                >
                                    {slide.meta}
                                </div>
                            )}
                            <div
                                className="font-display mt-2.5"
                                style={{ fontSize: '1.35rem', lineHeight: 1.2, color: 'var(--color-ink-700)', letterSpacing: '-0.015em' }}
                            >
                                {slide.title}
                            </div>
                            {slide.body && (
                                <p
                                    className="mt-2.5 text-[13.5px] leading-relaxed"
                                    style={{
                                        color: 'var(--color-ink-400)',
                                        display: '-webkit-box',
                                        WebkitLineClamp: slide.image ? 2 : 5,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {slide.body}
                                </p>
                            )}
                            <SlideCta slide={slide} />
                        </div>
                    </motion.div>
                </AnimatePresence>
            </div>

            <div
                className="flex items-center justify-between px-4 py-3"
                style={{ borderTop: '1px solid var(--color-rule)' }}
            >
                <button
                    type="button"
                    onClick={() => go(-1)}
                    aria-label="Previous announcement"
                    className="inline-flex items-center justify-center transition-colors hover:text-[var(--color-gold-600)]"
                    style={{ width: '1.8rem', height: '1.8rem', color: 'var(--color-ink-400)' }}
                >
                    <ChevronLeft size={17} />
                </button>
                <div className="flex items-center gap-2" role="tablist" aria-label="Slides">
                    {slides.map((s, i) => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => setIndex(i)}
                            aria-label={`Go to announcement ${i + 1}`}
                            aria-current={i === index}
                            style={{
                                width: i === index ? '1.4rem' : '0.45rem',
                                height: '0.45rem',
                                borderRadius: '999px',
                                background: i === index ? 'var(--color-gold-500)' : 'var(--color-rule-strong)',
                                transition: 'width 0.35s, background 0.35s'
                            }}
                        />
                    ))}
                </div>
                <button
                    type="button"
                    onClick={() => go(1)}
                    aria-label="Next announcement"
                    className="inline-flex items-center justify-center transition-colors hover:text-[var(--color-gold-600)]"
                    style={{ width: '1.8rem', height: '1.8rem', color: 'var(--color-ink-400)' }}
                >
                    <ChevronRight size={17} />
                </button>
            </div>

            <Lightbox
                items={viewing?.image ? [{ kind: 'image', src: viewing.image, caption: viewing.title }] : []}
                openIndex={viewing?.image ? 0 : null}
                onClose={() => setViewing(null)}
            />
        </div>
    )
}
